import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  AppBar,
  Toolbar,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import HomeIcon from "@mui/icons-material/Home";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const DocsPage: React.FC = () => {
  const [markdownContent, setMarkdownContent] = useState<string>("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string>("");
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    // Fetch the markdown content from the USER_DOCUMENTATION.md file
    fetch("/USER_DOCUMENTATION.md")
      .then((response) => response.text())
      .then((text) => setMarkdownContent(text))
      .catch((error) => console.error("Error loading documentation:", error));
  }, []);

  // Extract table of contents from markdown
  const extractTOC = (markdown: string) => {
    const headings: { id: string; title: string; level: number }[] = [];
    const lines = markdown.split("\n");

    lines.forEach((line) => {
      const match = line.match(/^(#{1,3})\s+(.+)$/);
      if (match) {
        const level = match[1].length;
        const title = match[2].trim();
        const id = title
          .toLowerCase()
          .replace(/[^\w\s-]/g, "")
          .replace(/\s+/g, "-");
        headings.push({ id, title, level });
      }
    });

    return headings;
  };

  const toc = extractTOC(markdownContent);

  const handleSectionClick = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      setActiveSection(id);
      if (isMobile) setDrawerOpen(false);
    }
  };

  // Custom renderer for markdown headings to add IDs (no automatic placeholder images)
  const components = {
    h1: ({ node, ...props }: any) => {
      const id = props.children
        ?.toString()
        .toLowerCase()
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, "-");
      return (
        <Typography variant="h3" id={id} sx={{ mt: 4, mb: 2, fontWeight: 700 }} {...props} />
      );
    },
    h2: ({ node, ...props }: any) => {
      const id = props.children
        ?.toString()
        .toLowerCase()
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, "-");
      return (
        <Typography variant="h4" id={id} sx={{ mt: 3, mb: 2, fontWeight: 600 }} {...props} />
      );
    },
    h3: ({ node, ...props }: any) => {
      const id = props.children
        ?.toString()
        .toLowerCase()
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, "-");
      return (
        <Typography variant="h5" id={id} sx={{ mt: 2, mb: 1.5, fontWeight: 500 }} {...props} />
      );
    },
    h4: ({ node, ...props }: any) => {
      const id = props.children
        ?.toString()
        .toLowerCase()
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, "-");
      return (
        <Typography variant="h6" id={id} sx={{ mt: 2, mb: 1, fontWeight: 500 }} {...props} />
      );
    },
    img: ({ node, ...props }: any) => (
      <Box
        component="img"
        src={props.src}
        alt={props.alt || "Documentation image"}
        sx={{
          maxWidth: "100%",
          height: "auto",
          borderRadius: 1,
          my: 2,
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      />
    ),
    p: ({ node, ...props }: any) => <Typography variant="body1" sx={{ mb: 2 }} {...props} />,
    code: ({ node, inline, ...props }: any) =>
      inline ? (
        <Box
          component="code"
          sx={{
            backgroundColor: "#f5f5f5",
            padding: "2px 6px",
            borderRadius: "4px",
            fontFamily: "monospace",
            fontSize: "0.9em",
          }}
          {...props}
        />
      ) : (
        <Box
          component="pre"
          sx={{
            backgroundColor: "#f5f5f5",
            padding: 2,
            borderRadius: 1,
            overflow: "auto",
            mb: 2,
          }}
        >
          <code style={{ fontFamily: "monospace" }} {...props} />
        </Box>
      ),
    table: ({ node, ...props }: any) => (
      <Box sx={{ overflowX: "auto", mb: 2 }}>
        <table style={{ borderCollapse: "collapse", width: "100%" }} {...props} />
      </Box>
    ),
    th: ({ node, ...props }: any) => (
      <th
        style={{
          backgroundColor: "#FFBF00",
          padding: "12px",
          textAlign: "left",
          borderBottom: "2px solid #ddd",
        }}
        {...props}
      />
    ),
    td: ({ node, ...props }: any) => (
      <td
        style={{
          padding: "12px",
          borderBottom: "1px solid #ddd",
        }}
        {...props}
      />
    ),
    a: ({ node, ...props }: any) => (
      <a
        style={{ color: "#FFBF00", textDecoration: "none", fontWeight: 500 }}
        {...props}
      />
    ),
    blockquote: ({ node, ...props }: any) => (
      <Box
        component="blockquote"
        sx={{
          borderLeft: "4px solid #FFBF00",
          paddingLeft: 2,
          margin: "16px 0",
          fontStyle: "italic",
          color: "#666",
        }}
        {...props}
      />
    ),
    ul: ({ node, ...props }: any) => (
      <ul style={{ paddingLeft: "24px", marginBottom: "16px" }} {...props} />
    ),
    ol: ({ node, ...props }: any) => (
      <ol style={{ paddingLeft: "24px", marginBottom: "16px" }} {...props} />
    ),
    li: ({ node, ...props }: any) => (
      <li style={{ marginBottom: "8px" }} {...props} />
    ),
  };

  const drawerContent = (
    <Box sx={{ width: 280, pt: 2 }}>
      <Typography variant="h6" sx={{ px: 2, mb: 2, fontWeight: 600 }}>
        Table of Contents
      </Typography>
      <Divider />
      <List>
        {toc.map((item, index) => (
          <ListItem key={index} disablePadding sx={{ pl: item.level * 2 }}>
            <ListItemButton
              onClick={() => handleSectionClick(item.id)}
              selected={activeSection === item.id}
              sx={{
                "&.Mui-selected": {
                  backgroundColor: "rgba(255, 191, 0, 0.1)",
                  borderLeft: "3px solid #FFBF00",
                },
              }}
            >
              <ListItemText
                primary={item.title}
                primaryTypographyProps={{
                  fontSize: item.level === 1 ? "0.95rem" : "0.85rem",
                  fontWeight: item.level === 1 ? 600 : 400,
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      {/* App Bar */}
      <AppBar position="sticky" sx={{ backgroundColor: "#000" }}>
        <Toolbar>
          {isMobile && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={() => setDrawerOpen(true)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
            LetsLaunch Documentation
          </Typography>
          <Button
            variant="outlined"
            startIcon={<HomeIcon />}
            onClick={() => navigate("/")}
            sx={{
              color: "#FFBF00",
              borderColor: "#FFBF00",
              "&:hover": {
                borderColor: "#FFBF00",
                backgroundColor: "rgba(255, 191, 0, 0.1)",
              },
            }}
          >
            Back to App
          </Button>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box sx={{ display: "flex", flex: 1 }}>
        {/* Desktop Sidebar */}
        {!isMobile && (
          <Drawer
            variant="permanent"
            sx={{
              width: 280,
              flexShrink: 0,
              "& .MuiDrawer-paper": {
                width: 280,
                boxSizing: "border-box",
                top: 64,
                height: "calc(100% - 64px)",
              },
            }}
          >
            {drawerContent}
          </Drawer>
        )}

        {/* Mobile Drawer */}
        {isMobile && (
          <Drawer
            anchor="left"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
          >
            {drawerContent}
          </Drawer>
        )}

        {/* Documentation Content */}
        <Container maxWidth="lg" sx={{ flex: 1, py: 4 }}>
          <Paper elevation={0} sx={{ p: 4, minHeight: "calc(100vh - 120px)" }}>
            <ReactMarkdown components={components} remarkPlugins={[remarkGfm]}>
              {markdownContent}
            </ReactMarkdown>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
};

export default DocsPage;
